import React from 'react'
import { Box, Typography } from '@mui/material'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import { BADGE_SIZE } from './constants'

/**
 * One badge: artwork, name, and whatever credit information the designation
 * allows. The locked variant (AC6) wears the real artwork behind a blur so the
 * donor can see that something is waiting without seeing what it is.
 *
 * The blur is presentation only - the image URL is still in the markup. That is
 * a deliberate call: a badge is not a secret, and serving a separate placeholder
 * asset would cost a round trip to hide something a determined donor could read
 * off the configuration screen anyway.
 */
export const BadgeTile = ({
  name,
  imageUrl,
  locked = false,
  creditsRequired = null,
  creditsNeeded = null,
  width,
}) => (
  <Box
    sx={{
      flex: width ? `0 0 ${width}` : '0 0 auto',
      width,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      gap: 0.75,
      p: 1.5,
      borderRadius: '10px',
      border: '1px solid',
      borderColor: 'divider',
      backgroundColor: 'background.paper',
    }}
  >
    <Box
      sx={{
        position: 'relative',
        width: BADGE_SIZE,
        height: BADGE_SIZE,
        flexShrink: 0,
      }}
    >
      {imageUrl ? (
        <Box
          component='img'
          src={imageUrl}
          alt=''
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            /* Enough that the detail cannot be read, not so much that the badge
               stops being a badge (AC6). The donor should still recognise the
               shape and colour of what is waiting for them - they are being
               shown a goal, not denied a glimpse of it. */
            filter: locked ? 'blur(2.5px)' : 'none',
          }}
        />
      ) : (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            backgroundColor: 'secondary.main',
          }}
        />
      )}

      {locked && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* A dark disc roughly a third of the badge wide. Big enough to
              carry a white lock against any artwork, small enough to leave the
              badge readable around it - at half the width it covered the part
              worth seeing. */}
          <Box
            sx={{
              width: BADGE_SIZE * 0.36,
              height: BADGE_SIZE * 0.36,
              borderRadius: '50%',
              backgroundColor: 'rgba(17, 17, 17, 0.82)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <LockRoundedIcon sx={{ color: '#fff', fontSize: 19 }} />
          </Box>
        </Box>
      )}
    </Box>

    <Typography variant='body2' fontWeight={700} sx={{ lineHeight: 1.3 }}>
      {name}
    </Typography>

    {/* AC6 / AC8: absent entirely when the designation hides credits. */}
    {creditsRequired != null && (
      <Typography variant='caption' sx={{ color: 'text.secondary' }}>
        {creditsRequired.toLocaleString()} credits
      </Typography>
    )}

    {locked && creditsNeeded != null && (
      <Typography
        variant='caption'
        sx={{
          mt: 0.25,
          px: 1,
          py: 0.25,
          borderRadius: '6px',
          backgroundColor: 'secondary.main',
          color: 'primary.main',
          fontWeight: 600,
        }}
      >
        {creditsNeeded.toLocaleString()} credits to go
      </Typography>
    )}
  </Box>
)
